'use client';

import { useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type Separator = 'colon' | 'hyphen' | 'none';
type Casing = 'lower' | 'upper';
type AdminBit = 'random' | 'universal' | 'local';
type CastBit = 'random' | 'unicast' | 'multicast';

const SEPARATORS: Record<Separator, string> = {
  colon: ':',
  hyphen: '-',
  none: '',
};

function randomByte(): number {
  return Math.floor(Math.random() * 256);
}

function buildFirstOctet(admin: AdminBit, cast: CastBit): number {
  let octet = randomByte();
  // Bit 0 (0x01) = individual/group (0 unicast, 1 multicast).
  if (cast === 'unicast') {
    octet &= ~0x01;
  } else if (cast === 'multicast') {
    octet |= 0x01;
  }
  // Bit 1 (0x02) = universal/local (0 universal, 1 locally administered).
  if (admin === 'universal') {
    octet &= ~0x02;
  } else if (admin === 'local') {
    octet |= 0x02;
  }
  return octet & 0xff;
}

function generateMac(separator: Separator, casing: Casing, admin: AdminBit, cast: CastBit): string {
  const bytes = [buildFirstOctet(admin, cast)];
  for (let i = 1; i < 6; i += 1) {
    bytes.push(randomByte());
  }
  const hex = bytes.map((b) => b.toString(16).padStart(2, '0'));
  const cased = casing === 'upper' ? hex.map((h) => h.toUpperCase()) : hex;
  return cased.join(SEPARATORS[separator]);
}

export default function BrowserRandomMacGenerator() {
  const [count, setCount] = useState(10);
  const [separator, setSeparator] = useState<Separator>('colon');
  const [casing, setCasing] = useState<Casing>('lower');
  const [admin, setAdmin] = useState<AdminBit>('random');
  const [cast, setCast] = useState<CastBit>('random');
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleGenerate() {
    setCopyMessage(null);
    const safeCount = Math.max(1, Math.min(5000, Math.floor(count) || 0));
    if (safeCount < 1) {
      setErrorMessage('Enter how many addresses to generate (1 to 5000).');
      setOutput('');
      return;
    }
    setErrorMessage(null);
    setCount(safeCount);

    const rows: string[] = [];
    for (let i = 0; i < safeCount; i += 1) {
      rows.push(generateMac(separator, casing, admin, cast));
    }
    setOutput(rows.join('\n'));
  }

  async function handleCopy() {
    if (!output) {
      return;
    }
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the addresses.');
    }
  }

  const lineCount = output ? output.split('\n').length : 0;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">MAC addresses</span>
        <h2 className="tool-live-title">Generate random MAC addresses</h2>
        <p className="tool-live-subtitle">
          Create random MAC addresses with your chosen separator, case, and administration bits for
          tests and mock inventory. Runs fully in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>How many</span>
              <input
                min="1"
                max="5000"
                onChange={(e) => setCount(Number(e.target.value))}
                type="number"
                value={count}
              />
            </label>
            <label className="tool-live-field">
              <span>Separator</span>
              <select onChange={(e) => setSeparator(e.target.value as Separator)} value={separator}>
                <option value="colon">Colon (00:1a:2b)</option>
                <option value="hyphen">Hyphen (00-1a-2b)</option>
                <option value="none">None (001a2b)</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Case</span>
              <select onChange={(e) => setCasing(e.target.value as Casing)} value={casing}>
                <option value="lower">Lowercase</option>
                <option value="upper">Uppercase</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Administration</span>
              <select onChange={(e) => setAdmin(e.target.value as AdminBit)} value={admin}>
                <option value="random">Random</option>
                <option value="universal">Universally administered</option>
                <option value="local">Locally administered</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Transmission</span>
              <select onChange={(e) => setCast(e.target.value as CastBit)} value={cast}>
                <option value="random">Random</option>
                <option value="unicast">Unicast</option>
                <option value="multicast">Multicast</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleGenerate} type="button">
              Generate
            </button>
            <button
              className="button button-secondary"
              disabled={!output}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
          </div>
          {errorMessage ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {errorMessage}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Addresses{lineCount ? ` (${lineCount})` : ''}</span>
            <textarea
              placeholder="Press Generate to build a list of random MAC addresses."
              readOnly
              rows={16}
              value={output}
            />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
